import { useMemo } from 'react';
import useAuthStore from '../stores/authStore';
import useStaffProfileStore from '../stores/staffProfileStore';
import { USER_ROLE } from '../types/auth';
import type { Permission } from '../types/permissions';

export interface PermissionsApi {
	/** Raw permission names granted to the signed-in user. */
	permissions: Set<string>;
	/** True when the user holds every listed permission. */
	has: (...required: Permission[]) => boolean;
	/** True when the user holds at least one of the listed permissions. */
	hasAny: (...required: Permission[]) => boolean;
	/**
	 * False while the staff profile that carries the permissions is still
	 * loading. Callers that would otherwise flash an "Access Denied" during the
	 * first render should wait on this.
	 */
	isLoaded: boolean;
}

/**
 * Permission checks for the signed-in user.
 *
 * Super admins and salon owners own their account outright and are not governed
 * by the role catalogue, so every check passes for them. Staff are governed by
 * the `auth.permissions` list on `GET /staff/me`; when that block is missing
 * (staff with no role assigned yet) they hold nothing and every gated surface
 * stays closed. These are UI affordances only — the backend enforces the same
 * rules on every request.
 */
const usePermissions = (): PermissionsApi => {
	const user = useAuthStore(state => state.user);
	const profile = useStaffProfileStore(state => state.profile);
	const profileError = useStaffProfileStore(state => state.profileError);

	const isStaff = user?.role === USER_ROLE.STAFF;
	const isPrivileged =
		user?.role === USER_ROLE.SUPER_ADMIN || user?.role === USER_ROLE.SALON_OWNER;

	const permissions = useMemo(() => {
		if (!isStaff) return new Set<string>();
		return new Set(profile?.auth?.permissions ?? []);
	}, [isStaff, profile]);

	return useMemo(() => {
		const has = (...required: Permission[]) =>
			isPrivileged || required.every(p => permissions.has(p));
		const hasAny = (...required: Permission[]) =>
			isPrivileged || required.some(p => permissions.has(p));

		return {
			permissions,
			has,
			hasAny,
			isLoaded: isPrivileged || !isStaff || !!profile || !!profileError,
		};
	}, [isPrivileged, isStaff, permissions, profile, profileError]);
};

export default usePermissions;
